<script setup lang="ts">
import BwrSelect from './form-fields/Select.vue'
import PurchaseTabOnce from './PurchaseTabOnce.vue'
import PurchaseTabSubscribe from './PurchaseTabSubscribe.vue'

import type { Product } from '@/lib/types/Product'
import type { PurchaseData } from '@/lib/types/PurchaseData'
import type { User } from '@/lib/types/User'
import type { WorkState } from '@/lib/types/WorkState'
import money from '../lib/formatters/money'
import { usePurchaseState } from './lib/PurchaseState'

const props = withDefaults(
  defineProps<{
    product: Product
    user: User
    initialWhen?: 'now' | 'next'
    loginLink?: string
    customerPortalLink?: string
    nextOrderLink?: string
    purchaseState: WorkState
    initialSellingPlan?: string
    initialQuantity?: number
    initialVariantId?: string
    plasticWasteSaving?: {
      sku: string
      plastic_waste_saved_g: number
    }
  }>(),
  {
    initialWhen: 'now',
    loginLink: '#',
    customerPortalLink: '#',
    nextOrderLink: '#',
    initialQuantity: 1,
  },
)

const emit = defineEmits<{
  (e: 'interaction'): void
  (e: 'variantChange', variantId: string): void
  (e: 'purchase', purchaseData: PurchaseData): void
}>()

const {
  selectedVariantId,
  selectedVariant,
  selectedSellingPlanId,
  hasVariants,
  isSubscriptionProduct,
  price,
  subscribePrice,
  pricePer,
  savePercent,
  quantity,
  when,
  subscriptionToThisProduct,
  emitPurchaseEvent,
  url,
  nextOrderDate,
} = usePurchaseState(props, emit)
</script>

<template>
  <form class="Purchase" @submit.prevent>
    <div class="Stack">
      <div v-if="hasVariants" class="Purchase__VariantSelector">
        <span
          v-if="product.variantOptionLabel"
          class="Purchase__VariantSelectorLabel"
        >
          Choose {{ product.variantOptionLabel.toLowerCase() }}:
        </span>
        <div class="Purchase__VariantSelectorDropdown">
          <BwrSelect
            :value="selectedVariantId"
            :disabled="purchaseState === 'IN_PROGRESS'"
            @change="selectedVariantId = $event"
          >
            <option
              v-for="variantId in Object.keys(product.variants)"
              :key="variantId"
              :value="variantId"
            >
              {{ product.variants[variantId].title }} –
              {{ money(product.variants[variantId].price) }}
            </option>
          </BwrSelect>
        </div>
      </div>
      <PurchaseTabSubscribe
        v-if="isSubscriptionProduct && selectedSellingPlanId !== null"
        :price="price"
        :price-per="pricePer"
        :compare-at-price="selectedVariant.compareAtPrice"
        :allow-subscribe="isSubscriptionProduct"
        :subscribe-price="subscribePrice"
        :save-percent="savePercent"
        :quantity="quantity"
        :when="when"
        :selected-selling-plan-id="selectedSellingPlanId"
        :selling-plan-allocations="selectedVariant.sellingPlanAllocations"
        :is-logged-in="user.isLoggedIn"
        :user-load-state="user.loadState"
        :next-order-date="nextOrderDate"
        :login-link="loginLink"
        :subscription-to-this-product="subscriptionToThisProduct"
        :purchase-state="purchaseState"
        :customer-portal-link="customerPortalLink"
        :next-order-link="nextOrderLink"
        :available="selectedVariant.available"
        :pre-sale="product.preSale"
        :waitlist-page-url="product.waitlistPageUrl"
        :selected-variant-id="selectedVariantId"
        :has-variants="hasVariants"
        :variants="product.variants"
        :url="url"
        :in-tab="true"
        :show-inline-variant-selector="false"
        :variant-option-label="product.variantOptionLabel"
        :plastic-waste-saving="plasticWasteSaving"
        :show-link="true"
        :related-product-reviews="product.relatedProductReviews"
        @quantity:changed="quantity = $event"
        @when:changed="when = $event"
        @selected-selling-plan:changed="selectedSellingPlanId = $event"
        @purchase="emitPurchaseEvent"
      />
      <PurchaseTabOnce
        v-else
        :price="price"
        :price-per="pricePer"
        :compare-at-price="selectedVariant.compareAtPrice"
        :allow-subscribe="isSubscriptionProduct"
        :subscribe-price="subscribePrice"
        :save-percent="savePercent"
        :quantity="quantity"
        :when="when"
        :selected-selling-plan-id="selectedSellingPlanId"
        :selling-plan-allocations="selectedVariant.sellingPlanAllocations"
        :is-logged-in="user.isLoggedIn"
        :user-load-state="user.loadState"
        :next-order-date="nextOrderDate"
        :login-link="loginLink"
        :subscription-to-this-product="subscriptionToThisProduct"
        :purchase-state="purchaseState"
        :customer-portal-link="customerPortalLink"
        :next-order-link="nextOrderLink"
        :available="selectedVariant.available"
        :pre-sale="product.preSale"
        :waitlist-page-url="product.waitlistPageUrl"
        :selected-variant-id="selectedVariantId"
        :has-variants="hasVariants"
        :variants="product.variants"
        :url="url"
        :in-tab="true"
        :show-inline-variant-selector="false"
        :variant-option-label="product.variantOptionLabel"
        :plastic-waste-saving="plasticWasteSaving"
        :show-link="true"
        :related-product-reviews="product.relatedProductReviews"
        @quantity:changed="quantity = $event"
        @when:changed="when = $event"
        @selected-selling-plan:changed="selectedSellingPlanId = $event"
        @purchase="emitPurchaseEvent"
      />
    </div>
  </form>
</template>

<style>
.Purchase {
  color: var(--color-forest-100);
  font-family: var(--font-family-rund);
}

.Purchase__VariantSelector {
  display: flex;
  justify-content: center;
  align-items: center;
}

.Purchase__VariantSelectorLabel {
  flex-grow: 0;
  flex-shrink: 0;
  margin-right: 15px;
  font-family: var(--font-family-domaine);
  font-size: 18px;
}

@media (--mq-tablet-and-up) {
  .Purchase__VariantSelector {
    padding: 0;
  }
}
@media (--mq-phablet-and-up) {
  .Purchase__VariantSelectorLabel {
    font-size: 26px;
  }
}

.Purchase__VariantSelector--inline .Purchase__VariantSelectorLabel {
  font-size: 15px;
  font-family: var(--font-family-rund);
}

.Purchase__VariantSelectorDropdown {
  flex-grow: 1;
}

.Purchase__Tab {
  padding: 20px;
  background-color: var(--color-lichen-100);
  @media (--mq-landscape-tablet-and-up) {
    border-radius: 0 var(--radius-large) var(--radius-large) var(--radius-large);
    overflow: hidden;
  }
}

.Purchase .TabbedArea__Nav {
  @media (--mq-landscape-tablet-and-up) {
    padding-left: 0;
  }
}

.TabbedArea__NavItem .Label {
  font-weight: normal;
  margin: -6px 0;
}

.Purchase__Tab--only-tab {
  @media (--mq-landscape-tablet-and-up) {
    border-radius: var(--radius-large);
  }
}

.Purchase__TabTitle {
  font-family: var(--font-family-domaine);
  font-weight: normal;
  font-size: 26px;
  line-height: 32px;
  margin: 10px 0;
}

.Purchase__TopLine {
  display: flex;
}

.Purchase__PriceLine {
  flex-grow: 1;
  align-items: center;
}

/* .Purchase__PriceLineLeft,
.Purchase__PriceLineRight {
  flex-grow: 1;
}

.Purchase__PriceLineLeft {
  text-align: left;
}

.Purchase__PriceLineRight {
  text-align: right;
} */

.Purchase__Price {
  font-family: var(--font-family-domaine);
  font-weight: bold;
  font-size: 32px;
  line-height: 1.2;
  @media (--mq-landscape-tablet-and-up) {
    font-size: 40px;
  }
}

.Purchase__PriceCompare {
  font-family: var(--font-family-rund);
  color: var(--color-forest-50);
  font-size: 14px;
  margin-left: 10px;
  display: inline-block;

  @media (--mq-landscape-tablet-and-up) {
    font-size: 16px;
  }
}

.Purchase__PricePer {
  font-family: var(--font-family-rund);
  font-size: 14px;

  @media (--mq-landscape-tablet-and-up) {
    font-size: 16px;
  }
}

.Purchase__PricePer > s {
  color: var(--color-forest-50);
}

.Purchase__Options {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 0;

  @media (--mq-phablet-and-up) {
    gap: 12px;
  }
}

.Purchase__OptionsLabel {
  flex-shrink: 0;
  max-width: 130px;
  padding-right: 14px;
  font-family: var(--font-family-rund);
  font-size: 15px;
  line-height: 1;

  @media (--mq-phablet-and-up) {
    width: 30%;
  }
}

.Purchase__Option {
  flex-grow: 0;
}

.Purchase__OptionSpacer {
  flex-basis: 0;
  flex-grow: 1;
}

.Purchase__Option > select {
  width: 100%;
}

.Purchase__Option--wide {
  flex-grow: 1;
}

.Purchase__LoginNudge,
.Purchase__ViewLink {
  text-align: center;
  font-size: 15px;
}

.Purchase__FreeGiftPromo {
  display: flex;
  align-items: first-baseline;
  padding: 0 13px;

  & p {
    margin: 0;
  }

  & > svg {
    flex-grow: 0;
    flex-shrink: 0;
    margin-right: 16px;
  }

  & a > svg {
    vertical-align: sub;
    margin-left: 3px;
  }
}
</style>
